// HiveShare configuration

import type { ThumbnailConfig } from '../types/hiveshare';

// Cloudflare Account Configuration
export const CLOUDFLARE_CONFIG = {
  // Account ID - this should match the one used in edge functions
  ACCOUNT_ID: process.env.NEXT_PUBLIC_CLOUDFLARE_ACCOUNT_ID || 'ys64orqv99pkve9j',
  
  // Stream domain for customer videos
  get STREAM_DOMAIN() {
    return `customer-${this.ACCOUNT_ID}.cloudflarestream.com`;
  }
} as const;

// Default thumbnail configuration
export const THUMBNAIL_CONFIG: ThumbnailConfig = {
  animated: {
    time: '5s',        // Start 5 seconds into the video for better preview
    height: 300,       // Default height
    width: 400,        // Default width (16:9 aspect ratio)
    duration: '4s',    // 4 second animation
    fps: 10,           // 10 fps for smooth but efficient animation
    fit: 'crop'        // Crop to maintain aspect ratio
  },
  static: {
    height: 300,
    width: 400,
    time: '7s',        // Use 7 seconds for static thumbnails
    fit: 'crop'
  },
  responsive: {
    mobile: {
      height: 180,     // Smaller for mobile
      width: 240       // 4:3 aspect ratio for mobile cards
    },
    tablet: {
      height: 224,     // Medium size for tablets (must be even for Cloudflare)
      width: 300       
    },
    desktop: {
      height: 300,     // Full size for desktop
      width: 400
    }
  }
} as const;

// Thumbnail URL builders
export const buildThumbnailUrls = {
  /**
   * Build static thumbnail URL
   */
  static: (videoId: string, options: Partial<typeof THUMBNAIL_CONFIG.static> = {}) => {
    const config = { ...THUMBNAIL_CONFIG.static, ...options };
    const params = new URLSearchParams();
    
    if (config.time) params.set('time', config.time.toString());
    if (config.height) params.set('height', config.height.toString());
    if (config.width) params.set('width', config.width.toString());
    if (config.fit) params.set('fit', config.fit);
    
    const queryString = params.toString();
    return `https://${CLOUDFLARE_CONFIG.STREAM_DOMAIN}/${videoId}/thumbnails/thumbnail.jpg${queryString ? `?${queryString}` : ''}`;
  },

  /**
   * Build animated thumbnail URL
   */
  animated: (videoId: string, options: Partial<typeof THUMBNAIL_CONFIG.animated> = {}) => {
    const config = { ...THUMBNAIL_CONFIG.animated, ...options };
    const params = new URLSearchParams();
    
    if (config.time) params.set('time', config.time.toString());
    if (config.height) params.set('height', config.height.toString());
    if (config.width) params.set('width', config.width.toString());
    if (config.duration) params.set('duration', config.duration.toString());
    if (config.fps) params.set('fps', config.fps.toString());
    if (config.fit) params.set('fit', config.fit);
    
    const queryString = params.toString();
    return `https://${CLOUDFLARE_CONFIG.STREAM_DOMAIN}/${videoId}/thumbnails/thumbnail.gif${queryString ? `?${queryString}` : ''}`;
  }
} as const;

// Responsive thumbnail options
export const getResponsiveThumbnailOptions = (breakpoint: 'mobile' | 'tablet' | 'desktop' = 'desktop') => {
  return THUMBNAIL_CONFIG.responsive[breakpoint];
};

// Utility to detect if we're in a supported environment
export const isThumbnailSupported = () => {
  return Boolean(CLOUDFLARE_CONFIG.ACCOUNT_ID);
};

// Default export with all utilities
export default {
  CLOUDFLARE_CONFIG,
  THUMBNAIL_CONFIG,
  buildThumbnailUrls,
  getResponsiveThumbnailOptions,
  isThumbnailSupported
};